import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { NoData } from "../../StudentData/components/AlertComponents";
import { ACTION_DISPLAY_MAP } from "../utils";
import { getWellnessActionDisplay, isOtherWellnessAction } from "@/utils/wellnessActions";

const StudentEmotionTable = ({ studentEmotionData, studentEmailToNameMap }) => {
  const containerRef = useRef(null);
  const [displayedData, setDisplayedData] = useState([]);
  const [dataIndex, setDataIndex] = useState(50);

  const sortedStudentData = useMemo(() => {
    return [...studentEmotionData].sort((a, b) => {
      if (b.count === a.count) {
        return b.percent - a.percent;
      }
      return b.count - a.count;
    });
  }, [studentEmotionData]);

  const getTopActions = useCallback(
    (studentEmail) => {
      const studentEntry = studentEmotionData.find(
        (student) => student.name === studentEmail
      );
      return studentEntry?.actions
        ? Object.entries(studentEntry.actions).sort(([, a], [, b]) => b - a)
        : [];
    },
    [studentEmotionData]
  );

  const truncateOtherActions = useCallback((action) => {
    if (!action) return "";
    const key = action.trim().toLowerCase();
    if (isOtherWellnessAction(action)) return "Other:";
    return ACTION_DISPLAY_MAP[key] || getWellnessActionDisplay(action);
  }, []);

  useEffect(() => {
    setDisplayedData(sortedStudentData.slice(0, 50));
    setDataIndex(50);
  }, [sortedStudentData]);

  const loadMoreData = useCallback(() => {
    if (dataIndex >= sortedStudentData.length) return;
    const newIndex = Math.min(dataIndex + 50, sortedStudentData.length);
    setDisplayedData(sortedStudentData.slice(0, newIndex));
    setDataIndex(newIndex);
  }, [dataIndex, sortedStudentData]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        scrollHeight - scrollTop <= clientHeight + 100 &&
        displayedData.length < sortedStudentData.length
      ) {
        loadMoreData();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [displayedData, sortedStudentData, loadMoreData]);

  if (sortedStudentData.length === 0) {
    return <NoData />;
  }

  return (
    <div>
      <div ref={containerRef} className="overflow-x-auto max-h-96">
        <div>
          {displayedData.map((student, index) => {
            const name = studentEmailToNameMap[student.name] || student.name;
            const topActions = getTopActions(student.name);
            const rawAction = topActions[0]?.[0] || "- -";
            const displayAction = truncateOtherActions(rawAction);

            return (
              <div key={index} className="border-b hover:bg-gray-50">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-1 sm:gap-2 py-2 2xl:p-3">
                  <div className="text-[16px] font-semibold sm:font-normal whitespace-normal break-words sm:truncate sm:max-w-[200px]">
                    {name}
                  </div>
                  <div className="grid grid-cols-[1fr_auto] sm:grid-cols-1">
                    <div className="text-[16px] whitespace-normal break-words sm:truncate sm:max-w-[200px]">
                      {displayAction}
                    </div>
                    <div className="flex sm:hidden items-center gap-2 text-[16px] font-semibold">
                      <span>{student.count}x</span>
                      <span className="text-[#000000]/30">|</span>
                      <span>{Math.round(student.percent)}%</span>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 text-[16px] font-semibold">
                    <span>{student.count}x</span>
                    <span className="text-[#000000]/30">|</span>
                    <span>{Math.round(student.percent)}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {displayedData.length < sortedStudentData.length && (
          <div className="p-4 text-center text-[12px] text-500">
            Loading more students...
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentEmotionTable;
